<template>
  <div id="app" :class="isLoaded ? false : 'is-loading'">
    <BigMenu v-show="isMenuOpen" :sound="sound" />
    <Navigation v-if="isLoaded" :isMenuOpen="isMenuOpen" />
    <Cart v-if="isCartOpen" />

    <div ref="smoothie" id="smoothie">
      <CanvasHero
        v-if="fetched && firstEnterInSlider"
        v-show="fetched && currentInSlider && getStateSlider"
      />

      <div>
        <router-view
          @loaded="afterData"
          :isFirstEnter="isFirstEnter"
          v-if="isLoaded"
          :key="key"/>
      </div>
    </div>

    <cookie-law theme="min" buttonText="Ok" transitionName="fade" buttonClass="cookie-button">
      <div slot="message">
        This website uses cookies. <a href="https://www.iubenda.com/privacy-policy/88693273/cookie-policy" target="_blank" rel="noopener">Learn More.</a>
      </div>
    </cookie-law>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import gsap from 'gsap'
import debounce from 'lodash/debounce'
import { Howl, Howler } from 'howler';

import EventBus from '@/scripts/event-bus'
import loop from '@/scripts/loop'

import BigMenu from '@/components/BigMenu'
import Navigation from '@/components/Navigation'
import Cart from '@/components/Cart'
import CanvasHero from '@/components/CanvasHero'

import CookieLaw from 'vue-cookie-law'

window.baseFetchUrl = './static/data/'

export default {
  name: 'App',

  components: {
    BigMenu,
    Navigation,
    Cart,
    CanvasHero,
    CookieLaw
  },

  data() {
    return {
      isLoaded: false,
      isFirstEnter: true,
      isHeroVisible: false,
      fetched: false,
      firstEnterInSlider: false,
      sound: {},
      hoverSound: {},
    }
  },

  computed: {
    ...mapState(['products', 'isMenuOpen', 'isCartOpen']),

    ...mapGetters(['getStateSlider', 'currentInSlider', 'getHover']),

    key() {
      return this.$route.name !== undefined
        ? this.$route.name
        : this.$route
    }
  },

  watch: {
    currentInSlider(newVal) {
      this.$store.commit('SET_STATE_HERO', newVal)
      if (!this.firstEnterInSlider && newVal) this.firstEnterInSlider = newVal
    },
  },

  created() {
    loop.start();
    this.sound = new Howl({
      src: ['/static/loop.mp3'],
      autoplay: true,
      loop: true,
      volume: 0,
    });

    this.handleResize = debounce(() => this.resize(), 100);
    window.addEventListener('resize', this.handleResize);
    this.resize();

    Promise.all([
      this.$store.dispatch('FETCH_DATA', 'global'),
      // this.$store.dispatch('FETCH_PRODUCTS'),
      this.getDataFromStore()
    ]).then(() => {
      const tl = new gsap.TimelineMax()
      this.isFirstEnter = true
      this.firstEnterInSlider = this.currentInSlider
      this.fetched = true
      this.sound.fade(0, 1, 600);

      tl
        .set('.wrapper', { autoAlpha: 0 })
        .add(() => {
          this.isLoaded = true
          this.$store.commit('SET_STATE_HERO', this.currentInSlider)
        })
        .to('.wrapper', 0.5, { autoAlpha: 1 })
        // .add(() => {
        //   EventBus.$emit('triggerRandomLight')
        // })
    })

    this.$router.beforeEach((to, from, next) => {
      this.isFirstEnter = false

      this.getDataFromStore(to).then(() => {
        EventBus.$emit('triggerRandomLight')
        // EventBus.$emit('slideHand')
        next()
      })
    })
  },

  methods: {
    afterData() {},

    onEnter(el, done) {
      gsap.fromTo(el, 1, {
        autoAlpha: 0
      }, {
        autoAlpha: 1,
        onComplete: done
      })
    },

    onLeave(el, done) {
      gsap.to(el, 1, {
        autoAlpha: 0,
        onComplete: done
      })
    },

    getDataFromStore(to) {
      const direction = to || this.$route

      return new Promise((resolve) => {
        this.$store.dispatch('FETCH_DATA', direction.name).then(() => resolve())
      })
    },

    resize() {
      EventBus.$emit('windowResize');

      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    },
  },
};
</script>

<style lang="scss">
@import "~sass-mq/mq";

body.progress {
  cursor: progress !important;
}

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  position: absolute;
  top: 0;
  left: 0;

  color: white;
  min-width: 100%;
  min-height: 100vh;
  min-height: calc(var(--vh, 1vh) * 100);

  &.is-loading {
    cursor: progress;
  }
}

#smoothie {
    @include boost-performance();
    // position: relative;

    // @include mq(sm) {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        width: 100vw;
        height: 100%;
    // }
}

:global() {
  .wrapper {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100vh;
    height: calc(var(--vh, 1vh) * 100);
    z-index: -1;
    overflow: hidden;

    canvas {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }
  }

  // .gradient {
  //   content: "";
  //   position: absolute;
  //   left: 0;
  //   right: 0;
  //   bottom: 0;
  //   height: 55%;
  //   z-index: 6;
  //   pointer-events: none;
  //   background: linear-gradient(to bottom, rgba(#000000, 0), #000000);
  // }

  [lazy] {
    transition: all 0.5s ease-out;
  }

  [lazy=loading] {
    opacity: 0;
  }

  [lazy=loaded] {
    opacity: 1;
  }
}

.scrollbar-track.scrollbar-track-y .scrollbar-thumb {
  border-radius: 0;
}

.Cookie.Cookie--min {
  width: 100%;
  font-size: font-size(s);
  z-index: z-index(loader);
  color: $color-blue;
  display: flex;
  flex-flow: row;
  padding: 15px 20px;
  background: #121212;
  font-size: 1em;

  @include mq(sm) {
    padding: 15px 20px 15px 30px;
    font-size: 0.875em;
    max-width: 500px;
    width: 60%;
    margin: 0 auto 35px;
  }

  @include mq(ds) {
    width: 40%;
  }

  a {
    opacity: 0.8;
    font-weight: 500;
    display: block;

    @include mq(sm) {
      display: inline-block;
    }
  }

  .Cookie__content {
    margin: 0;
    line-height: 1.5;
  }

  .cookie-button {
    background-color: #0072CE;
    border-radius: 20px;
    border: 0px;
    padding: 8px 24px;

    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 600;

    cursor: pointer;
    transition: opacity 0.6s cubic-bezier(0.075, 0.82, 0.165, 1);

    &:hover {
      opacity: 0.7;
    }
  }
}
</style>


    <!-- <div v-for="product in products" :key="product.title">
      {{product.title}}
      <ul v-for="variant in product.variants">
        <li>{{variant.title}}</li>
      </ul>
      <img :src="product.images[0].src" alt="aaa">
    </div> -->



// WEBPACK FOOTER //
// src/App.vue